import { createNoise2D, type NoiseFunction2D } from 'simplex-noise';
import { clamp01 } from '../../core/math';
import { resolvePalette, type ResolvedPalette } from '../../core/palette';
import type { RenderTarget, TechniqueContext } from '../types';
import type { DomainWarpValues } from './params';

/**
 * Domain warping applied to the glyph outlines.
 *
 * The classic construction (Quílez): displace a position by the value of a noise field,
 * then evaluate the *next* noise field at the displaced position, repeatedly. Each pass
 * folds the previous one, which is what produces the smoke-like swirls rather than a
 * simple wobble.
 *
 * Warping the *contours* rather than the pixels keeps the result resolution independent
 * and exportable as vectors — and lets the legibility damping read the distance field
 * per vertex, so the cores of the letters can stay put while the edges dissolve.
 */
export interface DomainWarpState {
  /** One entry per copy; each is the full contour set, warped. */
  copies: { points: Float32Array; starts: Uint32Array }[];
  palette: ResolvedPalette;
  noiseX: NoiseFunction2D;
  noiseY: NoiseFunction2D;
  built: number;
  /** How many copies this configuration asks for; `isComplete` only sees the state. */
  targetCopies: number;
}

function values(ctx: TechniqueContext): DomainWarpValues {
  return ctx.values as unknown as DomainWarpValues;
}

function fbm(noise: NoiseFunction2D, x: number, y: number, octaves: number, lacunarity: number, gain: number): number {
  let sum = 0;
  let amp = 1;
  let freq = 1;
  let norm = 0;
  for (let i = 0; i < octaves; i++) {
    sum += noise(x * freq, y * freq) * amp;
    norm += amp;
    amp *= gain;
    freq *= lacunarity;
  }
  return norm > 0 ? sum / norm : 0;
}

export function init(ctx: TechniqueContext): DomainWarpState {
  const v = values(ctx);
  return {
    copies: [],
    palette: resolvePalette(v.palette, ctx.rng.fork(1)),
    noiseX: createNoise2D(() => ctx.rng.next()),
    noiseY: createNoise2D(() => ctx.rng.next()),
    built: 0,
    targetCopies: v.copies,
  };
}

export function step(state: DomainWarpState, _dt: number, ctx: TechniqueContext): DomainWarpState {
  const v = values(ctx);
  if (state.built >= v.copies) return state;

  const copyIndex = state.built;
  // Later copies warp further, so a stack of them reads as a motion trail.
  const amount = v.copies > 1 ? v.strength * (0.25 + (copyIndex / (v.copies - 1)) * 1.1) : v.strength;

  const contours = ctx.field.contours;
  let total = 0;
  for (const contour of contours) total += contour.points.length;

  const points = new Float32Array(total);
  const starts = new Uint32Array(contours.length + 1);
  let cursor = 0;

  contours.forEach((contour, ci) => {
    starts[ci] = cursor / 2;
    const p = contour.points;
    for (let i = 0; i < p.length; i += 2) {
      let x = p[i];
      let y = p[i + 1];

      let scale = v.noiseScale;
      for (let pass = 0; pass < v.passes; pass++) {
        const wx = fbm(state.noiseX, x * scale, y * scale, v.octaves, v.lacunarity, v.gain);
        const wy = fbm(state.noiseY, x * scale, y * scale, v.octaves, v.lacunarity, v.gain);
        const passAmount = amount / v.passes;
        x += (wx + v.bias.x) * passAmount;
        y += (wy + v.bias.y) * passAmount;
        scale *= v.scalePerPass;
      }

      if (v.legibility > 0) {
        // Deep inside a glyph the displacement is pulled back towards the original
        // position; near the edge it is left alone.
        const depth = clamp01(-ctx.field.distance(p[i], p[i + 1]) / Math.max(1, v.legibilityReach));
        const hold = depth * v.legibility;
        x = p[i] + (x - p[i]) * (1 - hold);
        y = p[i + 1] + (y - p[i + 1]) * (1 - hold);
      }

      points[cursor++] = x;
      points[cursor++] = y;
    }
  });
  starts[contours.length] = cursor / 2;

  state.copies.push({ points, starts });
  state.built++;
  return state;
}

export function isComplete(state: DomainWarpState): boolean {
  return state.built >= state.targetCopies;
}

function buildPath(copy: { points: Float32Array; starts: Uint32Array }): Path2D {
  const path = new Path2D();
  for (let ci = 0; ci < copy.starts.length - 1; ci++) {
    const from = copy.starts[ci];
    const to = copy.starts[ci + 1];
    if (to - from < 3) continue;
    path.moveTo(copy.points[from * 2], copy.points[from * 2 + 1]);
    for (let i = from + 1; i < to; i++) path.lineTo(copy.points[i * 2], copy.points[i * 2 + 1]);
    path.closePath();
  }
  return path;
}

export function draw(state: DomainWarpState, target: RenderTarget, ctx: TechniqueContext): void {
  const v = values(ctx);
  const c = target.ctx;
  c.lineWidth = v.lineWidth;
  c.lineJoin = 'round';

  state.copies.forEach((copy, i) => {
    const t = state.copies.length > 1 ? i / (state.copies.length - 1) : 0;
    const color = state.palette.at(t, v.opacity);
    const path = buildPath(copy);
    if (v.style === 'fill') {
      c.fillStyle = color;
      c.fill(path, 'nonzero');
    } else {
      c.strokeStyle = color;
      c.stroke(path);
    }
  });
}

export function elementCount(state: DomainWarpState): number {
  let n = 0;
  for (const copy of state.copies) n += copy.points.length / 2;
  return n;
}

export function toSVG(state: DomainWarpState, ctx: TechniqueContext): string {
  const v = values(ctx);
  const f = (n: number): string => n.toFixed(2).replace(/\.?0+$/, '');
  const parts: string[] = [`<g stroke-width="${v.lineWidth}" stroke-linejoin="round">`];

  state.copies.forEach((copy, i) => {
    const t = state.copies.length > 1 ? i / (state.copies.length - 1) : 0;
    const color = state.palette.at(t);
    let d = '';
    for (let ci = 0; ci < copy.starts.length - 1; ci++) {
      const from = copy.starts[ci];
      const to = copy.starts[ci + 1];
      if (to - from < 3) continue;
      d += `M${f(copy.points[from * 2])} ${f(copy.points[from * 2 + 1])}`;
      for (let k = from + 1; k < to; k++) d += `L${f(copy.points[k * 2])} ${f(copy.points[k * 2 + 1])}`;
      d += 'Z';
    }
    if (!d) return;
    parts.push(
      v.style === 'fill'
        ? `<path fill="${color}" fill-opacity="${v.opacity}" fill-rule="nonzero" d="${d}"/>`
        : `<path fill="none" stroke="${color}" stroke-opacity="${v.opacity}" d="${d}"/>`,
    );
  });

  parts.push('</g>');
  return parts.join('\n');
}
